import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import ENDPOINTS from "../../config/apiUrls";
import { getRequest } from "../../config/dataApi";
import axiosInstance from "../../config/axiosInstance";
import { toast } from "react-toastify";


export const getTeam = createAsyncThunk("users/getTeam", async () => {
  try {
    const response = await getRequest(ENDPOINTS.USERS.TEAM);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to fetch members");
  }
});

export const updateTeamMember = createAsyncThunk('team/updateMember',
  async ({ message = 'Profile updated successfully', values, id }, { rejectWithValue, getState }) => {
    try {
      if (!id) {
        return rejectWithValue("User not found.");
      }

      const requestData = {
        data: {
          type: 'user',
          id: id,
          attributes: values
        },
      };
      const response = await axiosInstance.post(ENDPOINTS.USERS.UPDATE_MEMBER, requestData);
      if (response.status === 204) {
        toast.success(message, {
          position: "top-right",
          autoClose: 3000,
        });

        return { id, values };
      } else {
        return rejectWithValue('Failed to update profile.');
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'An error occurred.');
    }
  }
);

export const addTeamMember = createAsyncThunk('team/addMember',
  async ({ message = 'Profile updated successfully', palyload }, { dispatch ,rejectWithValue, getState }) => {
    try {
      const { auth } = getState();
      const alias = auth.user?.attributes.alias;

      const { id, values } = palyload;
      if (!id) {
        return rejectWithValue("User not found.");
      }
      delete values.email;
      values.alias = alias;
      const requestData = {
        data: {
          type: 'user',
          id: id,
          attributes: {alias ,...values}
        },
      };
      const response = await axiosInstance.post(ENDPOINTS.USERS.REGISTER_MEMBER, requestData);
      if (response.status === 200) {
        toast.success(message, {
          position: "top-right",
          autoClose: 3000,
        });

        dispatch(getTeam());
      } else {
        return rejectWithValue('Failed to update profile.');
      }
    } catch (error) {
      console.log(error.message);
      return rejectWithValue(error.response?.data?.message || 'An error occurred.');
    }
  }
);

export const blockUnblockUser = createAsyncThunk('team/blockUnblockUser',
  async ({ message = 'Profile updated successfully', values, id }, { rejectWithValue, getState }) => {
    try {
      if (!id) {
        return rejectWithValue("User not found.");
      }

      const requestData = {
        data: {
          type: 'user',
          id: id,
          attributes: values
        },
      };
      const response = await axiosInstance.post(ENDPOINTS.USERS.BAN, requestData);
      if (response.status === 204) {
        toast.success(message, {
          position: "top-right",
          autoClose: 3000,
        });

        return { id, values };
      } else {
        return rejectWithValue('Failed to update profile.');
      }
    } catch (error) {
      console.log(error.message);
      return rejectWithValue(error.response?.data?.message || 'An error occurred.');
    }
  }
);

export const passwordReset = createAsyncThunk('team/passwordReset',
  async ({ message = 'Profile updated successfully', id }, { rejectWithValue, getState }) => {
    try {
      if (!id) {
        return rejectWithValue("User not found.");
      }

      const requestData = {
        data: {
          type: 'user',
          id: id
        },
      };
      const response = await axiosInstance.post(ENDPOINTS.USERS.PASSWORD_RESET, requestData);
      if (response.status === 202) {
        toast.success(message, {
          position: "top-right",
          autoClose: 3000,
        });

        return true;
      } else {
        return rejectWithValue('Failed to update profile.');
      }
    } catch (error) {
      console.log(error.message);
      return rejectWithValue(error.response?.data?.message || 'An error occurred.');
    }
  }
);

export const deleteTeamMember = createAsyncThunk('team/deleteTeamMember',
  async ({ message = 'Team member deleted successfully', id }, { rejectWithValue, getState }) => {
    try {
      if (!id) {
        return rejectWithValue("User not found.");
      }
      const response = await axiosInstance.delete(ENDPOINTS.USERS.DELETE_MEMBER(id));
      if (response.status === 200) {
        
        toast.success(message, {
          position: "top-right",
          autoClose: 3000,
        });

        return id;
      } else {
        return rejectWithValue('Failed to delete team member.');
      }
    } catch (error) {
        return rejectWithValue(error?.response?.data?.errors[0]?.detail || 'An error occurred.');
    }
  }
);

const teamSlice = createSlice({
  name: "team",
  initialState: {
    members: [],
    totalPages: 1,
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getTeam.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTeam.fulfilled, (state, action) => {
        state.loading = false;
        state.members = action.payload.filter(
          row => row.attributes && Object.keys(row.attributes).length > 0
        );
      })
      .addCase(getTeam.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(updateTeamMember.fulfilled, (state, action) => {
        const { id, values } = action.payload;
        state.members = state.members.map(member =>
          member.id === id
            ? { ...member, attributes: { ...member.attributes, roles: values?.roles } }
            : member
        );
      })
      .addCase(deleteTeamMember.fulfilled, (state, action) => {
        state.loading = false;
        state.members = state.members.filter((member) => member.id !== action.payload);
      })
      .addCase(blockUnblockUser.fulfilled, (state, action) => {
        const { id, values } = action.payload;
        state.members = state.members.map(member =>
          member.id === id
            ? { ...member, attributes: { ...member.attributes, banned: values?.ban } }
            : member
        );
      });
  },
});

export default teamSlice.reducer;
